import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Grid,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  useMediaQuery,
  Dialog,
  DialogContent,
  OutlinedInput,
  ListSubheader,
} from '@mui/material';
import CustomDialogBox from '../../../../common/CustomDialogBox';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchDelightsQuery, usePostDelightMutation } from '../../delightApiSlice';
import { selectCurrentUser } from '../../../Auth/authSlice';
import UserAvatar from '../../../../common/UserAvatar';
import { useGetAllEmployeesQuery } from '../../../RnR/rnrApiSlice';
import FormSkeleton from '../../../RnR/skeleton/FormSkeleton';
import { useTheme } from '@emotion/react';
import Delights from '../DelightsData/Delights';
import { toggleSwipe } from '../../../Search/searchSlice';

const getTodayDate = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

const validationSchema = Yup.object().shape({
  requestType: Yup.string().required('Request type is required'),
  requestDate: Yup.date().required('Request date is required'),
  personName: Yup.array().min(1, 'At least one member is required').of(Yup.string().required()),
  otherDetails: Yup.string().required('Reason is required'),
});

const DelightsForm = () => {
  const user = useSelector(selectCurrentUser);
  const empCode = user?.empCode ?? '';
const inputRef = useRef();
  const { data: employeeData = [], isLoading: isEmployeeDataLoading } = useGetAllEmployeesQuery();
  const { data: delightsData = [], isLoading: isDelightsDataLoading } = useFetchDelightsQuery({ empCode });
  const [postDelight, { isLoading: isPosting }] = usePostDelightMutation();
  const [delightsDialogOpen, setDelightsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const dispatch = useDispatch();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(searchText);
    }, 2000); // Adjust delay (ms) as needed

    return () => clearTimeout(delayDebounce); // Clean up on next keystroke
  }, [searchText]);

  const handleDelightsDialogOpen = () => {
    setDelightsDialogOpen(true);
  };

  const handleDelightsDialogClose = () => {
    setDelightsDialogOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      requesterEmpCode: empCode,
      requestType: '',
      requestDate: '',
      numberOfMembers: 1,
      otherDetails: '',
      personName: [],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await postDelight(values);
        if (response) {
          toast.success('Request submitted successfully');
          resetForm();
          dispatch(toggleSwipe({ value: false }));
        }
      } catch (error) {
        toast.error(`Error submitting request: ${error.message}`);
      }
    },
  });

  const filteredEmployees = useMemo(() => {
    if (!employeeData?.length) return [];

    // Remove duplicates
    const unique = employeeData.filter((emp, index, self) => index === self.findIndex((e) => e._id === emp._id));

    if (!searchText.trim()) return unique.slice(0, 50); // Show only first 50 initially

    const searchLower = searchText.toLowerCase();
    return unique
      .filter((emp) => {
        const searchString = `${emp.firstName || ''} ${emp.middleName || ''} ${emp.lastName || ''} ${
          emp.userName || ''
        } ${emp.department || ''}`.toLowerCase();
        return searchString.includes(searchLower);
      })
      .slice(0, 30); // Limit search results
  }, [employeeData, searchText]);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  // Add this new handler to prevent Select's default keyboard behavior
  const handleSearchKeyDown = useCallback((e) => {
    // Prevent Select component from handling these keys
    e.stopPropagation();

    // Allow normal text input behavior
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

const handleDelete = (e, userNameToDelete) => {
  // e.stopPropagation();
  const newValues = formik.values.personName.filter(
    (name) => name !== userNameToDelete
  );
  formik.setFieldValue('personName', newValues);
  formik.setFieldValue('numberOfMembers', newValues.length);

  // Remove focus from the input
  // inputRef.current?.blur();
};


  const handleDialogOpen = () => {
    formik.setFieldValue('openDialog', true);
  };

  const handleDialogClose = () => {
    formik.setFieldValue('openDialog', false);
  };

  if (isEmployeeDataLoading || isDelightsDataLoading) {
    return <FormSkeleton />;
  }

  const getFontSize = () => {
    if (isXs) return '16px';
    if (isSm) return '18px';
    if (isMd) return '20px';
    if (isLg) return '22px';
    return '24px';
  };

  const getPadding = () => {
    if (isXs) return '5px';
    if (isSm) return '10px';
    // if (isMd) return '15px';
    // if (isLg) return '20px';
    return '5px';
  };

  //   const filteredEmployees = employeeData.filter((emp) => {
  //   const fullName = `${emp.firstName} ${emp.middleName} ${emp.lastName}`.toLowerCase();
  //   return fullName.includes(searchTerm.toLowerCase());
  // });

  return (
    <Box
      sx={{
        padding: getPadding(),
        maxWidth: '800px',
        margin: '0',
        paddingTop: '20px',
        maxHeight: '720px',
        overflowY: 'auto',
      }}
    >
      <Typography fontSize={getFontSize()} fontWeight="isSm?'0':bold" gutterBottom sx={{ mb: isSm ? '1rem' : 0 }}>
        Apply for Delights
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl
              sx={{ marginTop: '15px' }}
              fullWidth
              error={formik.touched.requestType && !!formik.errors.requestType}
            >
              <InputLabel id="request-type-label" sx={{ color: '#666' }}>
                Request Type*
              </InputLabel>
              <Select
                labelId="request-type-label"
                label="Request Type*"
                name="requestType"
                value={formik.values.requestType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                // required
              >
                <MenuItem value="Lunch">Lunch</MenuItem>
                <MenuItem value="Dinner">Dinner</MenuItem>
                <MenuItem value="Outing">Outing</MenuItem>
              </Select>
              {formik.touched.requestType && formik.errors.requestType && (
                <Typography variant="body2" color="error">
                  {formik.errors.requestType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Date*"
              type="date"
              name="requestDate"
              value={formik.values.requestDate}
              fullWidth
              onChange={formik.handleChange}
              onBlur={(e) => {
                const selectedDate = new Date(e.target.value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (selectedDate < today) {
                  toast.error('Past dates are not allowed');
                  formik.setFieldValue('requestDate', '');
                }

                formik.handleBlur(e); // still allow formik to track blur
              }}
              InputLabelProps={{ shrink: true, style: { color: '#666' } }}
              inputProps={{
                min: getTodayDate(), // still prevents using picker for past
                placeholder: '',
                style: { color: '#666' },
              }}
              error={formik.touched.requestDate && !!formik.errors.requestDate}
              helperText={formik.touched.requestDate && formik.errors.requestDate}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="select-members-label">Select Member*</InputLabel>
              <Select
                labelId="select-members-label"
                id="select-members"
                multiple
                value={formik.values?.personName}
                onChange={(event) => {
                  const selectedUsernames = event.target.value;
                  formik.setFieldValue('personName', selectedUsernames);
                  formik.setFieldValue('numberOfMembers', selectedUsernames.length);
                  setSearchTerm('');
                  setSearchText('');
                }}
                input={<OutlinedInput label="Select Member*" />}
                // Add these props to prevent auto-selection
                autoComplete="off"
                disableAutoFocus
                renderValue={(selected) => {
                  console.log('selected', selected.length, formik.values?.personName);

                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {selected.map((userName) => {
                        if (!userName) return;
                        const employee = employeeData.find((emp) => emp.userName === userName);
                        return (
                          <Chip
                            key={userName}
                            label={userName}
                            avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                            style={{ margin: 2 }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={(e) => handleDelete(e, userName)}
                          />
                        );
                      })}
                    </div>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 150,
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                    },
                  },
                  // Prevent menu from auto-focusing on items
                  // disableAutoFocusItem: true,
                  // // Prevent menu from restoring focus
                  // disableRestoreFocus: true,
                }}
              >
                {/* <ListSubheader> */}
              <TextField
  inputRef={inputRef}
  placeholder="Search member..."
  fullWidth
  value={searchText}
  onChange={handleSearchChange}
  onKeyDown={handleSearchKeyDown}
  onClick={(e) => e.stopPropagation()}
  size="small"
  InputProps={{
    autoComplete: 'off',
  }}
/>

                {/* </ListSubheader> */}

                {filteredEmployees.map((option) => (
                  <MenuItem
                    key={option._id || option.userName}
                    value={option.userName}
                    dense
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <UserAvatar size={28} src={option?.employeePhotoPath} />
                      <Box sx={{ marginLeft: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                          {`${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim()}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 11, color: '#666' }}>
                          {option.department}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}

                {filteredEmployees.length === 0 && (
                  <MenuItem disabled>
                    <Typography variant="body2" color="textSecondary">
                      No employees found
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {formik.touched.personName && formik.errors.personName && formik.values.personName.length === 0 && (
              <Typography variant="body2" color="error">
                {formik.errors.personName}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="otherDetails"
              label="Reason*"
              multiline
              rows={3}
              variant="outlined"
              value={formik.values.otherDetails}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              InputLabelProps={{ style: { color: '#666' } }}
              error={formik.touched.otherDetails && !!formik.errors.otherDetails}
              helperText={formik.touched.otherDetails && formik.errors.otherDetails}
            />
          </Grid>

          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth disabled={isPosting}>
              Submit
            </Button>
            <br />
          </Grid>
        </Grid>

        <CustomDialogBox
          open={formik.values.openDialog}
          handleClose={handleDialogClose}
          title="Confirm Submission"
          content={<Typography variant="body1">Are you sure you want to submit this request?</Typography>}
          actions={[
            { label: 'Confirm', onClick: formik.handleSubmit },
            { label: 'Cancel', onClick: handleDialogClose },
          ]}
        />
      </form>
    </Box>
  );
};

export default DelightsForm;
